
import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import { useAuth } from "../../../context/AuthContext";
import { generateDocCode } from "../../../utils/idGenerator";
import { createApprovalRequest, updateApprovalRequest, uploadApprovalFile } from "../../../services/approvalRequestsService";
import { DEPARTMENTS, getDeptNameByCode } from "../../../constants/departments";
import { useToast } from "../../../components/Toast";
import { getDeptCodeByEmail } from "../../../config/roles";
import { CONTRACT_TYPE_OPTIONS } from "../../../constants/contractTypes";
import { ApprovalModule } from "../../../types/legalOps";
import { AiFieldHeader } from "../../../components/shared/AiFieldHeader";
import { createOrResetApprovalFlow } from "../../../services/approvalFlowService";
import { analyzeUploadedFile } from "../../../services/aiService";
import { uploadTempFile } from "../../../services/storageUpload";
import { syncCrossReference } from "../../../services/crossReference";
import type { AttachmentMeta } from "../../../types/attachments";

interface Props {
  initialData: any | null;
  onClose: () => void;
  onSuccess: () => void;
  allExisting?: any[]; 
}

export function ContractReviewForm({ initialData, onClose, onSuccess, allExisting }: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<AttachmentMeta[]>(initialData?.attachments || []);

  const userDeptCode = getDeptCodeByEmail(user?.email);
  const userDeptName = getDeptNameByCode(userDeptCode);
  const lockedDept = userDeptName || DEPARTMENTS[0].name;

  const [form, setForm] = useState({
    department: initialData?.department || lockedDept,
    handlerName: (initialData as any)?.handlerName || user?.displayName || "",
    title: initialData?.title || "",
    counterparty: initialData?.counterparty || "",
    contractType: initialData?.contractType || "",
    priority: initialData?.priority || "normal", 
    description: initialData?.description || "",
    otherDeptComments: initialData?.otherDeptComments || "",
    docCode: initialData?.docCode || "",
    
    contractAmount: initialData?.contractAmount ? String(initialData.contractAmount) : "",
    contractCurrency: initialData?.contractCurrency || "TWD",
    
    archiveCode: initialData?.archiveCode || "",
    sealRequestCode: initialData?.sealRequestCode || ""
  });

  useEffect(() => {
    if (!initialData && !form.docCode) {
      const newCode = generateDocCode("contract", new Date(), allExisting || []);
      setForm((p) => ({ ...p, docCode: newCode }));
    }
  }, []);

  const handleAiClick = async () => {
      const hasPending = pendingFiles.length > 0;
      const hasExisting = uploadedAttachments.length > 0;

      if (!hasPending && !hasExisting) {
          showToast("請先上傳附件，再使用 AI 解析。", "error");
          return;
      }

      setAiLoading(true);
      try {
          let tempFiles: AttachmentMeta[] = [];
          
          if (hasPending) {
              setAttachmentUploading(true);
              for (const file of pendingFiles) {
                  const meta = await uploadTempFile(file);
                  tempFiles.push(meta);
              }
              setUploadedAttachments(prev => [...prev, ...tempFiles]);
              setAttachmentUploading(false);
          }

          const filesToAnalyze = [...uploadedAttachments, ...tempFiles];
          
          const result = await analyzeUploadedFile({
              files: filesToAnalyze,
              purpose: "contract_review"
          });

          const rawResult = result as any;
          const fields = rawResult.fields || rawResult; 

          setForm(prev => {
              const next = { ...prev };
              if (!prev.title && fields.title) next.title = fields.title;
              if (!prev.counterparty && fields.counterparty) next.counterparty = fields.counterparty;
              if (fields.amount) next.contractAmount = String(fields.amount).replace(/[^0-9.]/g, '');
              
              const summary = rawResult.summary || rawResult.description;
              if (summary) {
                  const oldDesc = prev.description || "";
                  next.description = oldDesc ? `${oldDesc}\n\n【AI 摘要】\n${summary}` : summary;
              }
              return next;
          });

          showToast("AI 解析完成，已填入建議欄位。");
      } catch(e: any) {
          console.error(e);
          showToast(`AI 解析失敗: ${e.message}`, "error");
      } finally {
          setAiLoading(false);
          setAttachmentUploading(false);
      }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.handlerName) {
      alert("請填寫必填欄位 (標題、承辦人)");
      return;
    }
    
    setLoading(true);
    try {
      let finalAttachments = initialData?.attachments || [];

      if (pendingFiles.length > 0) {
          for (const file of pendingFiles) {
              const meta = await uploadApprovalFile("temp_id", "request", file, user?.email || "");
              finalAttachments.push(meta);
          }
      }

      const effectiveDept = initialData?.id ? form.department : lockedDept;
      const amountVal = form.contractAmount ? Number(form.contractAmount) : null;
      
      const applicantDept = getDeptNameByCode(getDeptCodeByEmail(user?.email));
      const visibleDepartments = Array.from(new Set([effectiveDept, applicantDept].filter(Boolean)));

      const payload: any = {
        module: "contractDraft" as ApprovalModule,
        docCode: form.docCode,
        title: form.title,
        counterparty: form.counterparty,
        department: effectiveDept,
        contractType: form.contractType,
        priority: form.priority,
        description: form.description,
        consultedDepts: "", 
        coReviewDepts: [], 
        coReviewDepartments: [], 
        otherDeptComments: form.otherDeptComments,
        requesterName: form.handlerName,
        handlerName: form.handlerName,
        applicantDept: applicantDept,
        visibleDepartments: visibleDepartments,
        
        contractAmount: amountVal,
        contractCurrency: form.contractCurrency,
        
        archiveCode: form.archiveCode,
        sealRequestCode: form.sealRequestCode,
        attachments: finalAttachments
      };

      let docId = initialData?.id;
      if (docId) {
        await updateApprovalRequest(docId, payload);
      } else {
        // --- NEW: Enable Direct Seal Gate for new creation ---
        payload.directSealGate = {
            enabled: true,
            lisaDecision: "PENDING",
            ireneDecision: "PENDING",
            decidedAt: {},
            note: ""
        };

        docId = await createApprovalRequest({ 
            ...payload, 
            requesterUid: user?.id || "", 
            requesterEmail: user?.email || "", 
            sensitivity: "normal" 
        });
      }

      if (docId) {
          await createOrResetApprovalFlow(
              "CONTRACT_REVIEW", 
              docId,
              user?.id || "",
              effectiveDept,
              form.handlerName,
              { coReviewDepts: [] }
          );
          await updateApprovalRequest(docId, { status: 'IN_REVIEW' });
      }

      if (form.archiveCode) {
          await syncCrossReference('archive', form.archiveCode, { reviewCode: form.docCode, sealRequestCode: form.sealRequestCode });
      }
      if (form.sealRequestCode) {
          await syncCrossReference('seal', form.sealRequestCode, { reviewCode: form.docCode, archiveCode: form.archiveCode });
      }

      showToast("已送出合約審查，簽核流程啟動", "success");
      onSuccess();
    } catch (e: any) {
      console.error(e);
      showToast(`送出失敗: ${e.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = (idx: number, isPending: boolean) => {
      if (isPending) {
          setPendingFiles(prev => prev.filter((_, i) => i !== idx));
      } else {
          setUploadedAttachments(prev => prev.filter((_, i) => i !== idx));
      }
  };

  return (
    <Modal title={initialData ? "編輯合約審查" : "提出合約審查"} onClose={onClose} width={800}>
      <div className="grid">
        <div className="field">
          <label className="label">審查單號 (系統自動)</label>
          <input className="input" value={form.docCode} readOnly style={{ background: "rgba(255,255,255,0.05)", color: "#fbbf24", border: "1px dashed #555" }} />
        </div>
        <div className="field">
          <label className="label">承辦部門 (自動帶入)</label>
          <input className="input" value={form.department} readOnly style={{ background: "#222", color: "#aaa", cursor: "not-allowed", border: "1px solid #333" }} />
        </div>
        <div className="field"><label className="label">承辦人 <span style={{ color: "red" }}>*</span></label><input className="input" value={form.handlerName} onChange={(e) => setForm({ ...form, handlerName: e.target.value })} /></div>
        <div className="field">
          <label className="label">合約類型</label>
          <select className="input" value={form.contractType} onChange={(e) => setForm({ ...form, contractType: e.target.value })}>
            <option value="">請選擇</option>
            {CONTRACT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        
        <div className="field">
            <label className="label">對應合約歸檔編號 (系統自動回寫)</label>
            <input className="input" value={form.archiveCode || "尚無資料"} readOnly style={{background: "#222", color: "#666", cursor: "not-allowed"}} />
        </div>
        <div className="field">
            <label className="label">對應用印申請編號 (系統自動回寫)</label>
            <input className="input" value={form.sealRequestCode || "尚無資料"} readOnly style={{background: "#222", color: "#666", cursor: "not-allowed"}} />
        </div>

        <div className="field" style={{gridColumn: "1/-1"}}>
            <label className="label">速別 <span style={{ color: "red" }}>*</span></label>
            <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="normal">一般件（3-5天）</option>
                <option value="urgent">急件（1-3天）</option>
                <option value="express">特急件（1天）</option>
            </select>
        </div>

        <div className="field" style={{ gridColumn: "1/-1" }}><label className="label">合約案件名稱 <span style={{ color: "red" }}>*</span></label><input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div className="field" style={{ gridColumn: "1/-1" }}><label className="label">合約對象</label><input className="input" value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} /></div>
        
        <div className="field">
            <label className="label">合約總金額度 (若有)</label>
            <div style={{ display: "flex", gap: 8 }}>
                <select className="input" style={{ width: "100px" }} value={form.contractCurrency} onChange={(e) => setForm({ ...form, contractCurrency: e.target.value as any })}>
                    <option value="TWD">TWD</option>
                    <option value="USD">USD</option>
                    <option value="JPY">JPY</option>
                    <option value="KRW">KRW</option>
                </select>
                <input className="input" type="number" min={0} placeholder="0" value={form.contractAmount} onChange={(e) => setForm({ ...form, contractAmount: e.target.value })} />
            </div>
        </div>

        <div className="field" style={{ gridColumn: "1/-1" }}>
          <AiFieldHeader 
            label={attachmentUploading ? "附件上傳中..." : "需求說明 / 摘要 (AI 支援)"} 
            loading={aiLoading || attachmentUploading} 
            onClick={handleAiClick}
            disabled={pendingFiles.length === 0 && uploadedAttachments.length === 0} 
          />
          <textarea className="input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </div>

      <FileUploadKit
        label="參考附件 (PDF / Word)"
        existingFiles={uploadedAttachments}
        rawFiles={pendingFiles}
        onFilesSelected={(fs) => setPendingFiles(prev => [...prev, ...fs])}
        onRemoveRawFile={(idx) => handleRemoveFile(idx, true)}
        onRemoveFile={(f, idx) => handleRemoveFile(idx, false)}
      />

      <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}>
        <button className="btn" onClick={handleSubmit} disabled={loading || attachmentUploading || aiLoading}>{loading ? "處理中..." : "送出"}</button>
      </div>
    </Modal>
  );
}
